import React, { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faClose,
  faSpinner,
  faPlus,
} from "@fortawesome/free-solid-svg-icons";

import {
  mergeCustomerTags
} from "../../../../api/crm/customers/customerTagInfo";

import {
  showConfirmDialog,
  showSuccessToast,
  showErrorToast,
} from "../../../../components/ConfirmDialog";

// 🎨 COLOR PRESET
const COLOR_PRESET = [
  "#1E88E5",
  "#43A047",
  "#FB8C00",
  "#E53935",
  "#8E24AA",
  "#6D4C41",
  "#546E7A",
  "#FDD835",
];

const MergeTagDialog = ({ isOpen, onClose, oldTagIds = [], onMerged }) => {
  const [newName, setNewName] = useState("");
  const [color, setColor] = useState("#1E88E5");
  const [loading, setLoading] = useState(false);

  const resetForm = () => {
    setNewName("");
    setColor("#1E88E5");
  };

  const handleMerge = async () => {
    if (!newName.trim()) {
      showErrorToast("Vui lòng nhập tên tag mới");
      return;
    }

    const result = await showConfirmDialog({
      title: "Gộp tags?",
      text: `Tạo tag mới "${newName}" và gộp ${oldTagIds.length} tag vào nó?`,
      confirmText: "Gộp ngay",
      cancelText: "Hủy",
    });

    if (!result.isConfirmed) return;

    try {
      setLoading(true);

      await mergeCustomerTags({
        old_ids: oldTagIds,
        new_name: newName,
        new_color: color,
      });

      showSuccessToast("Gộp tag thành công");

      resetForm();
      onClose();
      onMerged?.();

    } catch (err) {
      console.error("Merge tag error:", err);
      showErrorToast("Gộp tag thất bại");
    } finally {
      setLoading(false);
    }
  };

  return (
    <AnimatePresence>
      {isOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-[1000]"
        >
          <motion.div
            initial={{ scale: 0.85, opacity: 0 }}
            animate={{ scale: 1, opacity: 1 }}
            exit={{ scale: 0.85, opacity: 0 }}
            className="bg-white w-[420px] max-w-[95%] rounded-2xl shadow-2xl p-6"
          >
            {/* HEADER */}
            <div className="flex justify-between items-center mb-4">
              <h2 className="text-lg font-semibold">
                Gộp {oldTagIds.length} tags thành 1
              </h2>
              <button onClick={onClose}>
                <FontAwesomeIcon
                  icon={faClose}
                  className="text-gray-500 text-lg hover:text-gray-700"
                />
              </button>
            </div>

            {/* FORM */}
            <div className="space-y-4">

              {/* TAG NAME */}
              <div>
                <label className="text-sm font-medium mb-1 block">
                  Tên tag mới
                </label>
                <input
                  type="text"
                  className="w-full border rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-600"
                  placeholder="Nhập tên tag mới..."
                  value={newName}
                  onChange={(e) => setNewName(e.target.value)}
                />
              </div>

              {/* COLOR PRESET */}
              <div>
                <label className="text-sm font-medium mb-1 block">
                  Màu tag mới
                </label>

                <div className="flex gap-2 mb-2">
                  {COLOR_PRESET.map((c) => (
                    <div
                      key={c}
                      onClick={() => setColor(c)}
                      className={`w-7 h-7 rounded-full cursor-pointer border shadow-sm transition
                        ${
                          color === c
                            ? "ring-2 ring-blue-500 scale-110"
                            : "hover:scale-105"
                        }
                      `}
                      style={{ backgroundColor: c }}
                    />
                  ))}
                </div>

                <input
                  type="color"
                  value={color}
                  onChange={(e) => setColor(e.target.value)}
                  className="w-16 h-10 border rounded cursor-pointer shadow-sm"
                />
              </div>

              {/* MERGE BUTTON */}
              <button
                onClick={handleMerge}
                disabled={loading}
                className={`
                  w-full py-2 rounded-lg text-white font-semibold
                  bg-gradient-to-r from-blue-600 to-blue-800 
                  transition-transform 
                  ${
                    loading
                      ? "opacity-50 cursor-not-allowed"
                      : "hover:scale-[1.02]"
                  }
                `}
              >
                {loading ? (
                  <FontAwesomeIcon icon={faSpinner} spin className="mr-2" />
                ) : (
                  <FontAwesomeIcon icon={faPlus} className="mr-2" />
                )}
                Gộp tags & tạo tag mới
              </button>
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default MergeTagDialog;
